import {
  FunctionComponent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';
import CloseIcon from 'components/icons/close';
import { authenticateWithoutRedirect } from 'components/login/actions';
import { createUrl } from 'lib/utils';
import { useRouter, useSearchParams } from 'next/navigation';
import { useFormState } from 'react-dom';
import ReactModal from 'react-modal';
import { CTAButton } from './click-to-purchase-cta';

import useCustomAnalyticsState from 'components/hooks/use-custom-analytics-state';
import { cleanQueryParams } from 'shared-react-components';
import './auth-modal.css';

interface SignInModalProps extends ReactModal.Props {
  guestEmail?: string;
  onRequestClose(): void;
  sku: string;
  quantity: number;
  productLine: string;
  funnelName: string;
  stepName: string;
  type: string;
  returnToOfferUrl: string;
  acceptOfferUrl: string;
}

const SignInModal: FunctionComponent<SignInModalProps> = ({
  guestEmail,
  onRequestClose,
  isOpen,
  sku,
  quantity,
  productLine,
  funnelName,
  stepName,
  type,
  returnToOfferUrl,
  acceptOfferUrl,
  ...rest
}) => {
  const { state } = useCustomAnalyticsState();
  const [email, setEmail] = useState(guestEmail);
  const [password, setPassword] = useState('');
  const [cartId, setCartId] = useState('');
  const [ancestor, setAncestor] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formMessage, dispatch] = useFormState(authenticateWithoutRedirect, undefined);
  const search = useSearchParams();
  const router = useRouter();

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  
  const queryParams = Object.fromEntries(cleanQueryParams(search));
  const sessionQueryParams = Object.keys(state?.page?.queryParams ?? {}).length !== 0 ? state?.page?.queryParams : null;
  const paramsContext = {
    ...(state?.sessionId ? {sessionId: state?.sessionId}: {}),
    ...(sessionQueryParams ?? queryParams)
  };

  useEffect(() => {
    if (formMessage === 'Success') {
      setLoading(true);
      const checkoutUrl = createUrl('/funnel-checkout', new URLSearchParams({
        cartId,
        productLine,
        funnelName,
        signInToPurchase: 'true',
        returnToOfferUrl,
        acceptOfferUrl,
        stepName,
        type,
        ancestor,
        ...paramsContext,
      }));
      router.push(checkoutUrl);
    } else if (formMessage) {
      setError(formMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMessage]);

  useEffect(() => {
    if (!isOpen) return;
    if (cartId.length) return;

    const createCart = async () => {
      try {
        const response = await fetch('/api/funnel-checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sku,
            quantity: quantity || 1,
            productLine,
            funnelName,
          }),
        });

        const result = await response.json();
        if (result.isSuccess) {
          setCartId(result.value?.cartId);
          setAncestor(result.value?.ancestorId?.toString() ?? '');
        } else {
          setError(result.message);
        }
      } catch (error) {
        console.error('Error:', error);
        setError('An unexpected error has occurred');
      }
    };

    createCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    ReactModal.setAppElement('html');
  }, []);

  useEffect(() => {
    if (emailInputRef.current && passwordInputRef.current) {
      setIsFormValid(
        emailInputRef.current.checkValidity() && passwordInputRef.current.checkValidity()
      );
    }
  }, [email, password]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => passwordInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const sendLoginEmail = async () => {
    if (emailInputRef.current && !emailInputRef.current.checkValidity()) {
      emailInputRef.current.reportValidity();
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/generate-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          cartId,
          productLine,
          funnelName,
          signInToPurchase: true,
          returnToOfferUrl,
          acceptOfferUrl,
          stepName,
          type,
          ancestor: Number(ancestor),
          paramsContext
        })
      });

      const result = await response.json();
      if (!result.isSuccess) {
        throw new Error(result.error || 'An unexpected error occurred');
      }

      setError('');
      setMessage(
        `Check your inbox. Your sign-in link is on its way to ${email}. This link expires in 5 minutes. If you don't see it, check your junk folder. Resend link or sign in using your password instead.`
      );
    } catch (error: any) {
      setError(error.message);
      setMessage('');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = useCallback(
    (event?: MouseEvent | KeyboardEvent) => {
      if (event) event.preventDefault();
      onRequestClose();
      document.body.focus();
    },
    [onRequestClose]
  );

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <ReactModal
      {...rest}
      isOpen={isOpen}
      ariaHideApp={process.env.NODE_ENV !== 'test'}
      bodyOpenClassName="has-activeModal"
      className={{
        base: 'auth-modal modalContent',
        afterOpen: 'modalContent--afterOpen',
        beforeClose: 'modalContent--beforeClose'
      }}
      closeTimeoutMS={200}
      onRequestClose={handleClose}
      overlayClassName={{
        base: 'modalOverlay',
        afterOpen: 'auth-modal modalOverlay--afterOpen',
        beforeClose: 'modalOverlay--beforeClose'
      }}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      onAfterClose={() => {
        setError('');
        setMessage('');
      }}
    >
      <header className="modal-header">
        <span className="modal-header-text">
          Sign In to Your Account to Claim Your Easy Payment Plan
        </span>

        <a className="modal-close" data-test="modal-close-button" href="#" onClick={handleClose}>
          <CloseIcon />
        </a>
      </header>

      <div className="modal-body" data-test="modal-body">
        <p className="info">
          <b>Looks like you already have an account!</b> Sign in now to unlock the convenience,
          safety & security of automatic-payment processing.
        </p>

        <form action={dispatch}>
          <div className="floating-label-group">
            <input
              type="email"
              value={email}
              placeholder=""
              required
              onChange={(e) => setEmail(e.target.value)}
              ref={emailInputRef}
              name="email"
            />
            <label className="floating-label">Email Address *</label>
          </div>
          <div className="floating-label-group">
            <input
              type={passwordVisible ? 'text' : 'password'}
              value={password}
              required
              placeholder=" "
              pattern="^(?=.*[a-zA-Z])(?=.*\d)[\w\W]{7,}$"
              title="Password must be at least 7 characters long and contain both letters and numbers."
              onChange={(e) => setPassword(e.target.value)}
              ref={passwordInputRef}
              name="password"
            />
            <label className="floating-label">Password *</label>
            <a onClick={togglePasswordVisibility} className="form-toggle-password form-input-icon">
              {passwordVisible ? <EyeIcon /> : <EyeSlashIcon />}
            </a>
          </div>
          <p className="forgot-password">
            Forgot your password?&nbsp;
            <button type="button" className="forgot-link" onClick={sendLoginEmail}>
              Email me a link
            </button>
            &nbsp;to sign in
          </p>
          <div className="buttons">
            <CTAButton
              buttonColor="Primary"
              buttonSize="Medium"
              buttonEmbellish="None"
              buttonFontSize={18}
              buttonText="Sign In & Continue"
              loading={loading}
              disabled={!!cartId.length && !isFormValid}
              type="submit"
            />

            <button onClick={handleClose} className="btn-cancel" type="button">
              Cancel
            </button>
          </div>
        </form>
        {!!error && <p className="error-message">{error}</p>}
        {!!message && <p className="info-message">{message}</p>}
      </div>
    </ReactModal>
  );
};

export default SignInModal;
